/* eslint-disable react/no-array-index-key */
import ConsultationRichInput from '@brands/Components/Inputs/RichTextField/RichTextField';
import { useQuillMessageBindings } from '@brands/hooks/useQuillMessageBindings';
import { normalizeAdditionalInfo } from '@brands/Utils/additionalInfo';
import { formatDate } from '@brands/Utils/formatDate';
import { getPainTypeText } from '@brands/Utils/getFriendlyName';
import { mapPainLevelLabel } from '@brands/Utils/mapPainLevelLabel';
import { Divider } from '@mui/material';
import { Editor } from 'primereact/editor';
import React, { SetStateAction, useEffect, useRef, useState } from 'react';
import { FieldErrors, UseFormRegister, UseFormSetValue, UseFormTrigger } from 'react-hook-form';

import { CaseType, ICase } from '../../../../services/cases/types/ICase';
import { UserProfile, UserRoleName } from '../../../../services/identity/types/UserProfile';
import { updateOneReport } from '../../../../services/oneReport/updateOneReport';
import { checkCollapsibleHeaderColor } from '../../../../Utils/getCollapsibleHeader';
import { getPainReasonForVisitText } from '../../../PatientForms/PatientForm/DescribePainLevel/DescribePainLevel';
import { OralHealthReportSubmit } from '../../assets/types';
import ConsultationOHRHeader from '../../Components/ConsultationOHRHeader/ConsultationOHRHeader';
import styles from '../../styles.module.scss';

type SubjectiveSectionProps = {
  consultationForm: OralHealthReportSubmit;
  setConsultationForm: React.Dispatch<React.SetStateAction<OralHealthReportSubmit>>;
  thisCase: ICase;
  setCase: React.Dispatch<SetStateAction<ICase>>;
  userInfo: UserProfile;
  register: UseFormRegister<OralHealthReportSubmit>;
  setValue: UseFormSetValue<OralHealthReportSubmit>;
  trigger: UseFormTrigger<OralHealthReportSubmit>;
  errors: FieldErrors<OralHealthReportSubmit>;
};

const SubjectiveSection = ({
  consultationForm,
  setConsultationForm,
  thisCase,
  setCase,
  userInfo,
  register,
  setValue,
  trigger,
  errors,
}: SubjectiveSectionProps): JSX.Element => {
  const editorRef = useRef<any>(null);
  const submitInputRef = useRef<HTMLButtonElement>(null);
  useQuillMessageBindings(editorRef, submitInputRef);

  const saveCaseAttribute = async (attributeName: string, attributeValue: any): Promise<void> => {
    if (attributeValue) {
      const updateData = {
        [attributeName]: attributeValue,
      };

      const response = await updateOneReport(thisCase?.one_report.id || '', updateData, userInfo.role.name);

      setCase({
        ...thisCase,
        one_report: {
          ...response,
        },
      });
    }
  };

  const updateChiefComplaintProblem = async (): Promise<void> => {
    await updateOneReport(
      thisCase?.one_report.id || '',
      {
        chief_complaint_problem: thisCase?.case_intake_questions?.is_dental_emergency
          ? 'I have a Dental Emergency'
          : 'I have a Non-urgent Dental Concern',
      },
      userInfo.role.name,
    );
  };

  useEffect(() => {
    // Clinical Enablement cases have no chief complaint — the section is
    // hidden for them, and the value is derived from intake questions they
    // never answer, so writing one would invent a complaint.
    if (
      thisCase.type !== CaseType.clinical_enablement &&
      thisCase.one_report.chief_complaint_problem === null &&
      userInfo.role.name === UserRoleName.Provider
    ) {
      updateChiefComplaintProblem();
    }
  }, []);

  const patientAdditionalInfo = (thisCase.case_intake_questions?.additional_info || []).filter((info) =>
    Boolean(normalizeAdditionalInfo(info.additional_info)),
  );

  return (
    <div className={styles.subjectiveDiv}>
      <span className={styles.subjectiveTitle}>Subjective</span>
      <div className={styles.subjectiveDivContainer}>
        {thisCase.type !== CaseType.clinical_enablement && (
          <>
        <div className={styles.subjective}>
          <span className={`fs-unmask ${styles.subjectiveSpan}`}>Chief Submitted Complaint/Problem:</span>
          <span className={`fs-exclude ${styles.subjectiveSpan} ${styles.subjectiveSpanValue}`}>
            {thisCase?.case_intake_questions?.is_dental_emergency
              ? 'I have a Dental Emergency'
              : 'I have a Non-urgent Dental Concern'}
          </span>
        </div>
        <div className={styles.subjectiveRow}>
          <div className={styles.subjective}>
            <span className={`fs-unmask ${styles.subjectiveSpan}`}>Pain level</span>
            <span className={`fs-exclude ${styles.subjectiveSpan} ${styles.subjectiveSpanValue}`}>
              {mapPainLevelLabel(thisCase?.case_intake_questions?.pain_level || '')}
            </span>
          </div>
          <Divider orientation="vertical" flexItem className={styles.divider} />
          <div className={styles.subjective}>
            <span className={`fs-unmask ${styles.subjectiveSpan}`}>Pain type</span>
            <span className={`fs-exclude ${styles.subjectiveSpan} ${styles.subjectiveSpanValue}`}>
              {thisCase?.case_intake_questions?.pain_description?.map((pain) => getPainTypeText(pain)).join(', ') ||
                '-'}
            </span>
          </div>
          <Divider orientation="vertical" flexItem className={styles.divider} />
          <div className={styles.subjective}>
            <span className={`fs-unmask ${styles.subjectiveSpan}`}>Time in pain</span>
            <span className={`fs-exclude ${styles.subjectiveSpan} ${styles.subjectiveSpanValue}`}>
              {' '}
              {thisCase?.case_intake_questions?.pain_time || '-'}
            </span>
          </div>
        </div>
        <div className={styles.subjective}>
          <span className={`fs-unmask ${styles.subjectiveSpan}`}>Symptoms</span>
          <span className={`fs-exclude ${styles.subjectiveSpan} ${styles.subjectiveSpanValue}`}>
            {thisCase?.case_intake_questions?.reason_for_visit?.length
              ? thisCase.case_intake_questions.reason_for_visit
                  .map((pain) => getPainReasonForVisitText(pain, thisCase?.case_intake_questions?.is_dental_emergency))
                  .join(', ')
              : '-'}
          </span>
        </div>
          </>
        )}
        <div className={styles.subjective}>
          <span className={`fs-unmask ${styles.subjectiveSpan}`}>Additional comments from patient</span>
          <div className={`fs-exclude ${styles.subjectiveSpanValue} ${styles.caseNotes}`}>
            {patientAdditionalInfo.length ? (
              patientAdditionalInfo.map((info, index) => (
                <div className={styles.caseNotesContent} key={index}>
                  <div className={styles.caseNotesContentMeta}>
                    <b>{formatDate(info.created_at)}</b>
                  </div>
                  <span className={`${styles.caseNotesContentText} ${styles.preserveLineBreaks}`}>
                    {normalizeAdditionalInfo(info.additional_info)}
                  </span>
                  <hr className={styles.ohrHr} />
                </div>
              ))
            ) : (
              <span>-</span>
            )}
          </div>
        </div>

        <div className={`${styles.subjective} ${styles.medicalHistoryReview}`}>
          <span className={`fs-unmask ${styles.subjectiveSpan}`}>Additional comments from provider</span>
          <ConsultationRichInput
            value={consultationForm.subjective.medical_history}
            editable={
              checkCollapsibleHeaderColor(thisCase, userInfo, 'Assessment') === 'edit' ||
              checkCollapsibleHeaderColor(thisCase, userInfo, 'Assessment') === 'editNotRequired'
            }
            onChange={(html) => {
              setConsultationForm((prev) => ({
                ...prev,
                subjective: { ...prev.subjective, medical_history: html },
              }));
              setValue('subjective.medical_history', html);
              if (errors?.subjective?.medical_history) trigger('subjective.medical_history');
            }}
            onBlurSave={async (html) => saveCaseAttribute('medical_history', html)}
            registerRef={(ref) => register('subjective.medical_history').ref(ref)}
            error={!!errors?.subjective?.medical_history}
            placeholder="Add addition comments here"
            className={`fs-exclude ${styles.subjectiveInput}`}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(SubjectiveSection);
